(() => {
  if (window.SunlitUpsellCardLoaded) return;
  window.SunlitUpsellCardLoaded = true;

  const moneySafeMessage = 'Could not add it right now. Please refresh and try again.';
  let cleanupInProgress = false;
  let cleanupTimer = null;
  let shopifyEventsPromise = null;

  function getShopifyEvents() {
    if (!shopifyEventsPromise) shopifyEventsPromise = import('@shopify/events');
    return shopifyEventsPromise;
  }

  async function beginCartLineUpdate(target, config) {
    try {
      const events = await getShopifyEvents();
      const deferred = events.CartLinesUpdateEvent.createPromise();
      target.dispatchEvent(new events.CartLinesUpdateEvent({ ...config, promise: deferred.promise }));
      return { deferred, events };
    } catch (error) {
      console.warn('Sunlit upsell cart event bridge unavailable:', error);
      return null;
    }
  }

  function resolveCartLineUpdate(bridge, cart, detail) {
    if (!bridge) return;
    bridge.deferred.resolve({
      cart: bridge.events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail,
    });
  }

  function getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
      .filter((value, index, array) => array.indexOf(value) === index);
  }

  function isUpsellItem(item) {
    const properties = item && item.properties ? item.properties : {};
    const source = properties['Added from'] || properties._sunlit_upsell || properties['_Sunlit upsell source'] || '';
    return typeof source === 'string' && source.toLowerCase().includes('upsell');
  }

  function renderCartNotice(text) {
    const drawer = document.querySelector('cart-drawer-component, .cart-drawer');
    const target = drawer?.querySelector('.cart-drawer__content, cart-items-component') || document.querySelector('cart-items-component');
    if (!target) return null;
    let notice = target.querySelector('.sunlit-upsell-cart-notice');
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'sunlit-upsell-cart-notice';
      notice.dataset.sunlitUpsellCartNotice = '';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      target.insertBefore(notice, target.firstElementChild?.nextSibling || target.firstChild);
    }
    notice.textContent = text;
    return notice;
  }

  function showCartNotice(text) {
    if (!text) return;
    renderCartNotice(text);
    showCartNotice._observer?.disconnect();
    const drawer = document.querySelector('cart-drawer-component, .cart-drawer');
    if (drawer) {
      showCartNotice._observer = new MutationObserver(() => {
        if (!drawer.querySelector('[data-sunlit-upsell-cart-notice]')) renderCartNotice(text);
      });
      showCartNotice._observer.observe(drawer, { childList: true, subtree: true });
    }
    window.clearTimeout(showCartNotice._timer);
    showCartNotice._timer = window.setTimeout(() => {
      showCartNotice._observer?.disconnect();
      document.querySelectorAll('[data-sunlit-upsell-cart-notice]').forEach((notice) => notice.remove());
    }, 6000);
  }


  function shuffled(items) {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }
    return result;
  }

  function updateScrollControls(list) {
    const scroller = list?.querySelector('.sunlit-upsell-list__scroller');
    const previous = list?.querySelector('[data-sunlit-upsell-prev]');
    const next = list?.querySelector('[data-sunlit-upsell-next]');
    if (!scroller || !previous || !next) return;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    previous.disabled = scroller.scrollLeft <= 2;
    next.disabled = maxScroll <= 2 || scroller.scrollLeft >= maxScroll - 2;
  }

  function moveUpsellScroller(button, direction) {
    const list = button.closest('[data-sunlit-upsell-list]');
    const scroller = list?.querySelector('.sunlit-upsell-list__scroller');
    if (!list || !scroller) return;
    const distance = Math.max(140, Math.round(scroller.clientWidth * 0.82));
    scroller.scrollBy({
      left: direction * distance,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    window.setTimeout(() => updateScrollControls(list), 320);
  }

  function rotateUpsellList(list) {
    const scroller = list && list.querySelector('.sunlit-upsell-list__scroller');
    if (!scroller) return;
    const allCards = Array.from(scroller.querySelectorAll('sunlit-upsell-card'));
    const cards = allCards.filter((card) => !card.classList.contains('is-added'));
    if (!allCards.length) return;

    const maxVisible = Math.max(1, Number(list.dataset.maxVisible) || 4);
    const chooseSelection = () => {
      if (list.dataset.recommendationMode === 'bundle-mix') {
        const greetings = shuffled(cards.filter((card) => card.dataset.category === 'greeting-card'));
        const postcards = shuffled(cards.filter((card) => card.dataset.category === 'postcard'));
        const balanced = [];
        const perType = Math.max(1, Math.floor(maxVisible / 2));
        balanced.push(...greetings.slice(0, perType), ...postcards.slice(0, perType));
        const remaining = shuffled(cards.filter((card) => !balanced.includes(card)));
        return shuffled([...balanced, ...remaining.slice(0, Math.max(0, maxVisible - balanced.length))]);
      }
      return shuffled(cards).slice(0, maxVisible);
    };

    const previous = list.dataset.visibleVariants || '';
    let selected = chooseSelection();
    let next = selected.map((card) => card.dataset.variantId).sort().join(',');
    for (let attempt = 0; attempt < 6 && cards.length > maxVisible && previous && next === previous; attempt += 1) {
      selected = chooseSelection();
      next = selected.map((card) => card.dataset.variantId).sort().join(',');
    }

    const selectedSet = new Set(selected);
    selected.forEach((card) => {
      card.hidden = false;
      scroller.appendChild(card);
    });
    cards.filter((card) => !selectedSet.has(card)).forEach((card) => {
      card.hidden = true;
      scroller.appendChild(card);
    });
    list.dataset.visibleVariants = next;
    scroller.scrollLeft = 0;
    window.requestAnimationFrame(() => updateScrollControls(list));
  }

  function rotateUpsellLists(selector = '[data-sunlit-upsell-list]') {
    document.querySelectorAll(selector).forEach(rotateUpsellList);
  }

  function updateUpsellButtonStates(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const variantIds = new Set(items.map((item) => String(item.variant_id)));
    const normalProductIds = new Set(
      items.filter((item) => !isUpsellItem(item)).map((item) => String(item.product_id))
    );

    document.querySelectorAll('[data-sunlit-upsell-list][data-source="product-page"]').forEach((list) => {
      const parentId = list.querySelector('sunlit-upsell-card[data-parent-product-id]')?.dataset.parentProductId || '';
      const parentInCart = Boolean(parentId && normalProductIds.has(String(parentId)));
      list.dataset.parentInCart = String(parentInCart);
      const notice = list.querySelector('[data-sunlit-upsell-parent-notice]');
      if (notice) notice.hidden = parentInCart;
    });

    document.querySelectorAll('sunlit-upsell-card[data-variant-id]').forEach((card) => {
      const button = card.querySelector('[data-sunlit-upsell-add]');
      if (!button) return;
      const parentRequired = card.dataset.source === 'product-page';
      const parentInCart = !parentRequired || normalProductIds.has(String(card.dataset.parentProductId || ''));
      card.dataset.parentInCart = String(parentInCart);

      if (variantIds.has(String(card.dataset.variantId))) {
        button.setAttribute('aria-disabled', 'true');
        setButtonState(card, 'added');
      } else if (!parentInCart) {
        button.setAttribute('aria-disabled', 'true');
        setButtonState(card, 'locked');
        setMessage(card, '');
      } else {
        button.removeAttribute('aria-disabled');
        setButtonState(card, 'default');
        setMessage(card, '');
      }
    });
  }

  async function syncUpsellButtons() {
    try {
      updateUpsellButtonStates(await fetchCart());
    } catch (error) {
      console.warn('Sunlit upsell state sync skipped:', error);
    }
  }

  function setButtonState(card, state) {
    const button = card.querySelector('[data-sunlit-upsell-add]');
    if (!button) return;
    const defaultLabel = button.querySelector('[data-label-default]');
    const loadingLabel = button.querySelector('[data-label-loading]');
    const addedLabel = button.querySelector('[data-label-added]');

    button.disabled = state === 'loading' || state === 'added' || state === 'locked';
    if (defaultLabel) defaultLabel.hidden = state === 'loading' || state === 'added';
    if (loadingLabel) loadingLabel.hidden = state !== 'loading';
    if (addedLabel) addedLabel.hidden = state !== 'added';
    card.classList.toggle('is-added', state === 'added');
  }

  function setMessage(card, text, isError = false) {
    const message = card.querySelector('[data-sunlit-upsell-message]');
    if (!message) return;
    message.textContent = text || '';
    message.classList.toggle('is-error', Boolean(isError));
  }

  function isCartDrawerOpen() {
    return Boolean(document.querySelector('#cart-drawer dialog[open], dialog.theme-drawer__dialog[open]'));
  }

  function refreshCartSections(parsedResponse, variantId) {
    const sections = parsedResponse && parsedResponse.sections ? parsedResponse.sections : {};
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: parsedResponse,
          data: {
            source: 'sunlit-upsell',
            variantId: variantId ? String(variantId) : undefined,
            itemCount: parsedResponse && parsedResponse.item_count,
            sections,
          },
        },
      })
    );
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Could not read cart.');
    return response.json();
  }

  async function applyDiscountCode(code) {
    const cleanCode = String(code || '').trim();
    if (!cleanCode) return false;
    try {
      await fetch(`/discount/${encodeURIComponent(cleanCode)}?redirect=/cart`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      return true;
    } catch (error) {
      console.warn('Sunlit upsell discount could not be applied:', error);
      return false;
    }
  }

  async function fetchFreshCartSections(sectionIds) {
    if (!sectionIds || !sectionIds.length) return {};
    const response = await fetch(`/cart?sections=${encodeURIComponent(sectionIds.join(','))}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) return {};
    return response.json().catch(() => ({}));
  }

  async function changeLine(key, quantity, includeSections = true) {
    const payload = { id: key, quantity };
    const sectionIds = getCartSectionIds();
    if (includeSections && sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const parsed = await response.json().catch(() => ({}));
    if (!response.ok || parsed.status >= 400) {
      throw new Error(parsed.description || parsed.message || moneySafeMessage);
    }
    return parsed;
  }

  async function cleanupLonelyUpsells() {
    if (cleanupInProgress) return;
    cleanupInProgress = true;
    let bridge = null;
    try {
      const cart = await fetchCart();
      const items = cart.items || [];
      const normalItems = items.filter((item) => !isUpsellItem(item));
      const normalProductIds = new Set(normalItems.map((item) => String(item.product_id)));
      const orphans = items.filter((item) => {
        if (!isUpsellItem(item)) return false;
        const properties = item.properties || {};
        const source = String(properties._sunlit_upsell || '').toLowerCase();
        const parentId = String(properties._sunlit_parent_product || '');
        if (source === 'product-page-upsell' && parentId) return !normalProductIds.has(parentId);
        return normalItems.length === 0;
      });

      if (!orphans.length) return;

      bridge = await beginCartLineUpdate(document.documentElement, {
        action: 'remove',
        context: 'cart',
        lines: orphans.map((item) => ({ id: item.key, quantity: 0 })),
      });
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: Object.fromEntries(orphans.map((item) => [item.key, 0])) }),
      });
      const updatedCart = await response.json().catch(() => ({}));
      if (!response.ok || updatedCart.status >= 400) {
        throw new Error(updatedCart.description || updatedCart.message || moneySafeMessage);
      }
      resolveCartLineUpdate(bridge, updatedCart, {
        items: updatedCart.items || [],
        source: 'sunlit-upsell-cleanup',
        itemCount: updatedCart.item_count,
        didError: false,
      });
      refreshCartSections(updatedCart);
      showCartNotice('The add-on was removed with its main product.\nTo keep an add-on, keep its main product in your cart.');
    } catch (error) {
      bridge?.deferred.reject(error);
      console.warn('Sunlit upsell cleanup skipped:', error);
    } finally {
      cleanupInProgress = false;
    }
  }

  function scheduleCleanup(delay = 700) {
    window.clearTimeout(cleanupTimer);
    cleanupTimer = window.setTimeout(cleanupLonelyUpsells, delay);
  }

  async function addUpsell(card) {
    const variantId = card.dataset.variantId;
    const button = card.querySelector('[data-sunlit-upsell-add]');
    if (!variantId || !button) return;

    const list = card.closest('[data-sunlit-upsell-list]');
    const parentInCart = card.dataset.parentInCart || list?.dataset.parentInCart;
    if (card.dataset.source === 'product-page' && parentInCart !== 'true') {
      setButtonState(card, 'locked');
      list?.querySelector('[data-sunlit-upsell-parent-notice]')?.removeAttribute('hidden');
      return;
    }

    setButtonState(card, 'loading');
    setMessage(card, '');

    const payload = {
      id: Number(variantId),
      quantity: 1,
      properties: {
        Offer: 'Add-on',
        '_Sunlit upsell source': card.dataset.source === 'cart-drawer' ? 'Cart drawer upsell' : 'Product page upsell',
        _sunlit_upsell: card.dataset.source === 'cart-drawer' ? 'cart-drawer-upsell' : 'product-page-upsell',
      },
    };
    if (card.dataset.source === 'product-page' && card.dataset.parentProductId) {
      payload.properties._sunlit_parent_product = card.dataset.parentProductId;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    let parsedResponse = {};
    try {
      parsedResponse = responseText ? JSON.parse(responseText) : {};
    } catch (error) {
      parsedResponse = {};
    }

    if (!response.ok || parsedResponse.status >= 400) {
      throw new Error(parsedResponse.description || parsedResponse.message || moneySafeMessage);
    }

    button.setAttribute('aria-disabled', 'true');
    setButtonState(card, 'added');
    setMessage(card, 'Added.');
    if (list) {
      card.hidden = true;
      window.setTimeout(() => rotateUpsellList(list), 80);
    }

    const discountCode = card.dataset.discountCode;
    Promise.resolve(discountCode ? applyDiscountCode(discountCode) : false)
      .then(() => fetchCart())
      .then(async (cart) => {
        updateUpsellButtonStates(cart);
        const bridge = await beginCartLineUpdate(card, {
          action: 'add',
          context: card.dataset.source === 'cart-drawer' ? 'cart' : 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        });
        resolveCartLineUpdate(bridge, cart, {
          items: cart.items || [],
          source: 'sunlit-upsell',
          itemCount: cart.item_count,
          productId: card.dataset.productId,
          didError: false,
        });
        refreshCartSections(cart, variantId);
      })
      .catch((error) => console.warn('Sunlit upsell cart refresh skipped:', error));

  }

  document.addEventListener('click', async (event) => {
    const previousControl = event.target.closest('[data-sunlit-upsell-prev]');
    const nextControl = event.target.closest('[data-sunlit-upsell-next]');
    if (previousControl || nextControl) {
      event.preventDefault();
      moveUpsellScroller(previousControl || nextControl, nextControl ? 1 : -1);
      return;
    }

    const button = event.target.closest('[data-sunlit-upsell-add]');
    if (!button) {
      if (event.target.closest('cart-items-component, cart-drawer-component, .cart-drawer')) scheduleCleanup(900);
      return;
    }

    const card = button.closest('sunlit-upsell-card');
    if (!card || button.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();

    try {
      await addUpsell(card);
    } catch (error) {
      console.error(error);
      setButtonState(card, 'default');
      setMessage(card, error.message || moneySafeMessage, true);
    }
  });

  document.addEventListener('scroll', (event) => {
    const scroller = event.target;
    if (!(scroller instanceof Element) || !scroller.matches('.sunlit-upsell-list__scroller')) return;
    updateScrollControls(scroller.closest('[data-sunlit-upsell-list]'));
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.closest('cart-items-component, cart-drawer-component, .cart-drawer')) scheduleCleanup(900);
  });

  document.addEventListener('cart:update', (event) => {
    window.setTimeout(() => {
      syncUpsellButtons();
      rotateUpsellLists('[data-sunlit-upsell-list][data-source="cart-drawer"]');
    }, 250);
    if (event.detail && event.detail.data && event.detail.data.source === 'sunlit-upsell') return;
    if (!isCartDrawerOpen()) return;
    scheduleCleanup(900);
  });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    event.promise
      ?.then(({ detail }) => {
        if ((detail?.source === 'sunlit-upsell' || detail?.source === 'sunlit-upsell-cleanup') && Array.isArray(detail.items)) {
          updateUpsellButtonStates({ items: detail.items });
        } else {
          syncUpsellButtons();
        }
        if (detail?.source === 'sunlit-upsell' || detail?.source === 'sunlit-upsell-cleanup') return;
        scheduleCleanup(40);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('Sunlit upsell cart sync skipped:', error);
        scheduleCleanup(180);
      });
  });

  function initUpsells() {
    getShopifyEvents().catch(() => null);
    rotateUpsellLists();
    syncUpsellButtons();
    document.querySelectorAll('[data-sunlit-upsell-list]').forEach(updateScrollControls);
    if (isCartDrawerOpen()) scheduleCleanup(900);
  }

  const drawerObserver = new MutationObserver((mutations) => {
    const opened = mutations.some((mutation) => mutation.type === 'attributes' && mutation.attributeName === 'open' && mutation.target.matches('#cart-drawer dialog, dialog.theme-drawer__dialog') && mutation.target.hasAttribute('open'));
    if (opened) {
      rotateUpsellLists('[data-sunlit-upsell-list][data-source="cart-drawer"]');
      syncUpsellButtons();
    }
  });

  drawerObserver.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsells, { once: true });
  } else {
    initUpsells();
  }
})();
